import type { Chart } from 'chart.js'
import {
  type ChartExplanationData,
  type ChartExplanationOptions,
  type ChartDataPoint,
  type ChartTooltipData,
  isChart
} from '@/types/chartExplanation'

export function useChartExplanation() {
  
  /**
   * Wait for chart animations to complete before extracting data
   */
  const waitForChartRender = (chart: Chart): Promise<void> => {
    return new Promise((resolve) => {
      if (chart.config.options?.animation === false) {
        // No animation, resolve immediately
        resolve()
        return
      }

      // Check if chart is already rendered
      if (!chart.isAnimating?.()) {
        resolve()
        return
      }

      // Wait for animation completion
      const checkComplete = () => {
        if (!chart.isAnimating?.()) {
          resolve()
        } else {
          requestAnimationFrame(checkComplete)
        }
      }
      
      requestAnimationFrame(checkComplete)
    })
  }

  /**
   * Extract tooltip data specific to your chart types
   */
  const extractTooltipData = (chart: Chart, index: number): ChartTooltipData | undefined => {
    const datasets = chart.data.datasets
    if (!datasets.length) return undefined

    // For lifeplan charts with rich metadata
    const primaryDataset = datasets[0]
    const metadata = (primaryDataset as any).meta

    if (metadata) {
      const tooltip: ChartTooltipData = {}
      
      // Extract financial data
      if (metadata.superGrowth && metadata.superGrowth[index] !== undefined) {
        tooltip.super = metadata.superGrowth[index]
      }
      
      if (metadata.assets) {
        tooltip.assets = metadata.assets.reduce((total: number, asset: any) => {
          const value = asset.data?.[index] || 0
          return total + value
        }, 0)
      }
      
      if (metadata.debtData) {
        tooltip.debts = metadata.debtData.reduce((total: number, debt: any) => {
          const value = debt.balances?.[index]?.balance || 0
          return total + value
        }, 0)
      }
      
      tooltip.netWealth = primaryDataset.data[index] as number
      
      // Extract milestone data if available
      if (datasets.length > 1 && datasets[1].data[index]) {
        const milestoneData = datasets[1].data[index] as any
        if (milestoneData?.label) {
          tooltip.milestone = milestoneData.label
        }
        if (milestoneData?.goal) {
          tooltip.goal = milestoneData.goal
        }
        if (milestoneData?.ages) {
          tooltip.ages = milestoneData.ages
        }
      }
      
      // Add year from labels
      if (chart.data.labels?.[index]) {
        tooltip.year = chart.data.labels[index].toString()
      }
      
      return tooltip
    }

    // For doughnut charts (engagement metrics)
    if (chart.config.type === 'doughnut') {
      const total = datasets[0].data.reduce((sum: number, value: any) => sum + (value || 0), 0)
      const value = datasets[0].data[index] as number
      
      return {
        percentage: Math.round((value / total) * 100),
        // These would come from component props
        activeMembers: (chart as any)._activeMembers,
        adviceIssued: (chart as any)._adviceIssued,
        moduleLabel: (chart as any)._moduleLabel
      }
    }

    return undefined
  }

  /**
   * Extract structured data points from the chart
   */
  const extractDataPoints = (chart: Chart, options: ChartExplanationOptions = {}): ChartDataPoint[] => {
    const labels = chart.data.labels || []
    const datasets = chart.data.datasets || []
    
    return labels.map((label, index) => {
      const values: Record<string, any> = {}
      
      // Extract values from all datasets
      datasets.forEach((dataset) => {
        const datasetName = dataset.label || `Dataset ${datasets.indexOf(dataset)}`
        values[datasetName] = dataset.data[index]
      })
      
      const dataPoint: ChartDataPoint = {
        index,
        label: label?.toString() || index.toString(),
        values
      }
      
      // Include tooltip data if requested
      if (options.includeTooltips) {
        dataPoint.tooltip = extractTooltipData(chart, index)
      }
      
      return dataPoint
    })
  }

  /**
   * Main function to extract all chart explanation data
   */
  const extractChartData = async (
    chart: Chart,
    options: ChartExplanationOptions = {}
  ): Promise<ChartExplanationData> => {
    if (!isChart(chart)) {
      throw new Error('Invalid chart instance provided')
    }

    // Wait for animations if requested
    if (options.waitForAnimation !== false) {
      await waitForChartRender(chart)
    }

    // Extract base64 image
    const image = chart.toBase64Image('image/png', 1)

    // Extract structured data points
    const dataPoints = extractDataPoints(chart, options)

    // Build explanation data
    const explanationData: ChartExplanationData = {
      image,
      type: chart.config.type as any,
      title: chart.config.options?.plugins?.title?.text as string,
      dataPoints,
      context: options.context || {}
    }

    // Include raw chart data if requested
    if (options.includeRawData) {
      explanationData.chartData = {
        labels: [...(chart.data.labels || [])],
        datasets: chart.data.datasets.map(dataset => ({
          label: dataset.label,
          data: [...dataset.data],
          type: dataset.type
        }))
      }
    }

    return explanationData
  }

  /**
   * Helper to validate chart before extraction
   */
  const validateChart = (chart: any): chart is Chart => {
    return isChart(chart) && chart.canvas && chart.data
  }

  return {
    extractChartData,
    extractDataPoints,
    extractTooltipData,
    waitForChartRender,
    validateChart
  }
}